import React, { useState, useRef, useEffect } from 'react';
import {
    Edit,
    TextInput,
    DateInput,
    SelectInput,
    useGetIdentity,
    useTranslate,
    TabbedForm,
    FormTab,
    required,
    useNotify,
    useRefresh,
} from 'react-admin';
import {
    Card, CardContent, Typography, Avatar, Box, Button,
    IconButton, CircularProgress, Badge,
    Alert, AlertTitle, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField,
} from '@mui/material';
import {
    PhotoCamera as CameraIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import { tokenService } from './services/tokenService';

const SSO_URL = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
const DELETE_REQUEST_URL = `${SSO_URL}/api/account/delete-request`;

interface DeletionRequest {
    id: number;
    status: string;
    requestedAt: string;
    dueAt: string;
}

const formatDate = (iso: string): string => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * DSGVO-Self-Service (Issues #206 + #207): Datenexport (Art. 15/20) und
 * Account-Loeschung (Art. 17) direkt aus der Profil-Maske.
 */
export const PrivacySection: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [exporting, setExporting] = useState<'pdf' | 'json' | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [withdrawing, setWithdrawing] = useState(false);
    const [deletionRequest, setDeletionRequest] = useState<DeletionRequest | null>(null);

    const loadDeletionRequest = async (isActive: () => boolean = () => true) => {
        try {
            const response = await fetch(DELETE_REQUEST_URL, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!response.ok) return;
            const data = await response.json();
            if (isActive()) setDeletionRequest(data.request ?? null);
        } catch {
            if (isActive()) setDeletionRequest(null);
        }
    };

    useEffect(() => {
        let active = true;
        loadDeletionRequest(() => active);
        return () => {
            active = false;
        };
    }, []);

    const handleExport = async (format: 'pdf' | 'json') => {
        setExporting(format);
        try {
            const response = await fetch(`${API_URL}/api/export/my-data${format === 'pdf' ? '.pdf' : ''}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = format === 'pdf'
                ? `online-academy-datenauskunft-${new Date().toISOString().slice(0, 10)}.pdf`
                : `online-academy-datenexport-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            notify(translate('privacy.exportSuccess', { _: 'Dein Datenexport wurde heruntergeladen.' }), { type: 'success' });
        } catch (e) {
            notify(translate('privacy.exportError', { _: 'Export fehlgeschlagen. Bitte versuche es erneut.' }), { type: 'error' });
        } finally {
            setExporting(null);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const response = await fetch(DELETE_REQUEST_URL, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.error || `HTTP ${response.status}`);
            }
            setDeletionRequest(data);
            notify(translate('privacy.deleteSuccess', {
                dueAt: formatDate(data.dueAt),
                _: `Dein Löschantrag ist eingegangen. Dein Unternehmens-Administrator entscheidet bis zum ${formatDate(data.dueAt)}.`,
            }), { type: 'info' });
            setConfirmText('');
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Fehler', { type: 'error' });
        } finally {
            setDeleting(false);
            setDeleteOpen(false);
        }
    };

    const handleWithdraw = async () => {
        setWithdrawing(true);
        try {
            const response = await fetch(DELETE_REQUEST_URL, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                if (response.status === 404) {
                    notify(data.error || `HTTP ${response.status}`, { type: 'error' });
                    await loadDeletionRequest();
                    return;
                }
                throw new Error(data.error || `HTTP ${response.status}`);
            }
            setDeletionRequest(null);
            notify(translate('privacy.withdrawSuccess', { _: 'Dein Löschantrag wurde zurückgezogen.' }), { type: 'info' });
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Fehler', { type: 'error' });
        } finally {
            setWithdrawing(false);
        }
    };

    const confirmWord = translate('privacy.deleteConfirmWord', { _: 'LÖSCHEN' });

    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {translate('privacy.title', { _: 'Datenschutz' })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('privacy.subtitle', { _: 'Lade eine vollständige Kopie deiner Daten herunter (Art. 15/20 DSGVO) oder lösche dein Konto (Art. 17 DSGVO).' })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        startIcon={exporting === 'pdf' ? <CircularProgress size={18} /> : <DownloadIcon />}
                        onClick={() => handleExport('pdf')}
                        disabled={exporting !== null}
                        data-testid="privacy-export-button"
                    >
                        {translate('privacy.exportButton', { _: 'Meine Daten herunterladen (PDF)' })}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={exporting === 'json' ? <CircularProgress size={18} /> : <DownloadIcon />}
                        onClick={() => handleExport('json')}
                        disabled={exporting !== null}
                        data-testid="privacy-export-json-button"
                    >
                        {translate('privacy.exportButtonJson', { _: 'Als JSON für andere Anbieter' })}
                    </Button>
                    {!deletionRequest && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteForeverIcon />}
                            onClick={() => setDeleteOpen(true)}
                            data-testid="privacy-delete-button"
                        >
                            {translate('privacy.deleteButton', { _: 'Konto löschen' })}
                        </Button>
                    )}
                </Box>
                {deletionRequest && (
                    <Alert
                        severity="info"
                        sx={{ mt: 2 }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleWithdraw}
                                disabled={withdrawing}
                                data-testid="privacy-withdraw-button"
                            >
                                {translate('privacy.withdrawButton', { _: 'Antrag zurückziehen' })}
                            </Button>
                        }
                    >
                        <AlertTitle>{translate('privacy.pendingTitle', { _: 'Löschantrag gestellt' })}</AlertTitle>
                        {translate('privacy.pendingText', {
                            requestedAt: formatDate(deletionRequest.requestedAt),
                            dueAt: formatDate(deletionRequest.dueAt),
                            _: `Du hast am ${formatDate(deletionRequest.requestedAt)} beantragt, dein Konto zu löschen. Dein Unternehmens-Administrator entscheidet bis zum ${formatDate(deletionRequest.dueAt)}.`,
                        })}
                    </Alert>
                )}
            </CardContent>

            <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
                <DialogTitle>{translate('privacy.deleteTitle', { _: 'Löschung deines Kontos beantragen?' })}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {translate('privacy.deleteWarning', { _: 'Dein Unternehmens-Administrator erhält deinen Antrag und entscheidet innerhalb eines Monats. Nach der Genehmigung wird dein Konto anonymisiert und nach der Aufbewahrungsfrist endgültig gelöscht. Entscheidet bis dahin niemand, wird es automatisch gelöscht. Bis zur Entscheidung kannst du dich weiter anmelden und den Antrag zurückziehen.' })}
                    </DialogContentText>
                    <TextField
                        fullWidth
                        label={translate('privacy.deleteConfirmLabel', { _: 'Zur Bestätigung "LÖSCHEN" eingeben' })}
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
                        {translate('privacy.cancel', { _: 'Abbrechen' })}
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={confirmText !== confirmWord || deleting}
                        data-testid="privacy-delete-confirm"
                    >
                        {deleting ? <CircularProgress size={18} /> : translate('privacy.deleteConfirmButton', { _: 'Löschung beantragen' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

const AvatarUpload: React.FC = () => {
    const { data: identity, refetch } = useGetIdentity();
    const notify = useNotify();
    const translate = useTranslate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const currentAvatar = previewUrl || (identity as any)?.avatar || null;
    const firstName = (identity as any)?.firstName || '';
    const lastName = (identity as any)?.lastName || '';
    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || 'U';

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate client-side
        if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
            notify(translate('profile.avatar.invalidType'), { type: 'error' });
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            notify(translate('profile.avatar.tooLarge'), { type: 'error' });
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const token = localStorage.getItem('token');
            const response = await fetch(`${SSO_URL}/api/me/avatar`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                setPreviewUrl(`${SSO_URL}${data.avatarUrl}`);
                notify(translate('profile.avatar.uploadSuccess'), { type: 'success' });
                // Clear cached identity so getIdentity() fetches fresh data from API
                tokenService.clearCachedIdentity();
                refetch?.();
            } else {
                const err = await response.json().catch(() => ({}));
                notify(err.error || translate('profile.avatar.uploadError'), { type: 'error' });
            }
        } catch {
            notify(translate('profile.avatar.uploadError'), { type: 'error' });
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async () => {
        setUploading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${SSO_URL}/api/me/avatar`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (response.ok) {
                setPreviewUrl(null);
                notify(translate('profile.avatar.deleteSuccess'), { type: 'success' });
                tokenService.clearCachedIdentity();
                refetch?.();
            }
        } catch {
            notify(translate('profile.avatar.deleteError'), { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, p: 2 }}>
            <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                    <IconButton
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' },
                            width: 32,
                            height: 32,
                        }}
                    >
                        {uploading ? <CircularProgress size={16} color="inherit" /> : <CameraIcon fontSize="small" />}
                    </IconButton>
                }
            >
                <Avatar
                    src={currentAvatar || undefined}
                    sx={{ width: 80, height: 80, fontSize: 28, bgcolor: '#c8ced8', color: '#1f3240' }}
                >
                    {initials}
                </Avatar>
            </Badge>

            <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                    {translate('profile.avatar.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {translate('profile.avatar.hint')}
                </Typography>
                {currentAvatar && (
                    <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleDelete}
                        disabled={uploading}
                    >
                        {translate('profile.avatar.remove')}
                    </Button>
                )}
            </Box>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
        </Box>
    );
};

const Profile = () => {
    const { data: identity, isLoading } = useGetIdentity();
    const translate = useTranslate();
    // E-Mail-Adressen sind der Login-Anker — Aenderungen sind Superadmin-Sache
    // (azubi:web-Integration 2026-09). Serverseitig erzwingt das die
    // EmailChangePolicy im SSO (PATCH /api/me).
    const emailEditable = ['ROLE_OPERATOR', 'ROLE_PLATFORM_ADMIN', 'ROLE_SUPER_ADMIN']
        .some((role) => tokenService.getRoles().includes(role));

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (!identity?.id) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6">
                        {translate('custom.profile.notLoggedIn')}
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
        <Edit
            resource="profile"
            id={identity.id}
            title={translate('custom.profile.title')}
            redirect={false}
            mutationMode="pessimistic"
        >
            <AvatarUpload />
            <TabbedForm syncWithLocation={false}>
                <FormTab label="resources.users.tabs.general">
                    <TextInput source="id" disabled fullWidth />
                    <TextInput
                        source="email"
                        validate={required()}
                        type="email"
                        fullWidth
                        disabled={!emailEditable}
                        helperText={emailEditable ? undefined : translate('custom.profile.emailLocked')}
                    />
                    <TextInput source="firstName" fullWidth />
                    <TextInput source="lastName" fullWidth />
                </FormTab>
                <FormTab label="resources.users.tabs.contact">
                    <TextInput source="phone" fullWidth />
                    <TextInput source="mobile" fullWidth />
                    <TextInput source="street" fullWidth />
                    <TextInput source="zip" />
                    <TextInput source="city" fullWidth />
                    <TextInput source="country" fullWidth />
                </FormTab>
                <FormTab label="resources.users.tabs.work">
                    <TextInput source="jobTitle" fullWidth />
                    <TextInput source="department.name" label="resources.users.fields.department" fullWidth disabled />
                </FormTab>
                <FormTab label="resources.users.tabs.personal">
                    <DateInput source="dateOfBirth" />
                    <SelectInput source="language" choices={[
                        { id: 'de', name: 'Deutsch' },
                        { id: 'en', name: 'English' },
                        { id: 'es', name: 'Español' },
                        { id: 'fr', name: 'Français' },
                        { id: 'it', name: 'Italiano' },
                    ]} />
                </FormTab>
            </TabbedForm>
        </Edit>
        <PrivacySection />
        </>
    );
};

export default Profile;
